/** StreakStore — ログインストリーク 7 日ボーナス管理。
 *  Reference: docs/PROPOSALS/RETENTION.md §R2
 *  - localStorage `ck.streak`: { currentDay, weekIndex, lastClaimDate }
 *  - 1 日 1 回 claimToday() → Color 付与
 *  - Week2 完走で +500 Color + フラグメント 1 枚
 *  - Pay-to-Win 禁止: ボーナスは時間短縮・装飾のみ(Skill Honored 準拠)
 */

export interface StreakState {
  /** 現在の連続日数(1–7 でサイクル) */
  currentDay: number;
  /** 何週目か(0 始まり) */
  weekIndex: number;
  /** 最後にクレームした日付文字列 yyyy-mm-dd */
  lastClaimDate: string;
}

export interface StreakClaimResult {
  /** 付与された Color */
  colorGained: number;
  /** 名画フラグメント付与(Week2 完走時) */
  fragmentGained: number;
  /** 今日既にクレーム済みだったか */
  alreadyClaimed: boolean;
  /** クレーム後の currentDay */
  newDay: number;
}

const STORAGE_KEY = 'ck.streak';

/** 曜日別 Color 報酬 (d1..d7) */
export const STREAK_REWARDS: readonly number[] = [100, 200, 300, 400, 500, 600, 1000] as const;

/** Week2 完走ボーナス */
const WEEK2_BONUS_COLOR = 500;
const WEEK2_BONUS_FRAGMENT = 1;

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export class StreakStore {
  private state: StreakState;

  constructor() {
    this.state = this.load();
  }

  private load(): StreakState {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return this.defaultState();
      const parsed = JSON.parse(raw) as Partial<StreakState>;
      return {
        currentDay:
          typeof parsed.currentDay === 'number' &&
          parsed.currentDay >= 1 &&
          parsed.currentDay <= 7
            ? parsed.currentDay
            : 1,
        weekIndex:
          typeof parsed.weekIndex === 'number' && parsed.weekIndex >= 0
            ? parsed.weekIndex
            : 0,
        lastClaimDate:
          typeof parsed.lastClaimDate === 'string' ? parsed.lastClaimDate : '',
      };
    } catch {
      return this.defaultState();
    }
  }

  private defaultState(): StreakState {
    return { currentDay: 1, weekIndex: 0, lastClaimDate: '' };
  }

  private save(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* noop(プライベートモード等) */
    }
  }

  /** 現在の連続日数(1–7) */
  getCurrentStreakDay(): number {
    return this.state.currentDay;
  }

  /** 何週目か(0 始まり) */
  getWeekIndex(): number {
    return this.state.weekIndex;
  }

  /** 今日既にクレーム済みか */
  hasClaimedToday(): boolean {
    return this.state.lastClaimDate === todayStr();
  }

  /** 今日のクレーム。当日初回のみ有効。
   *  - 報酬 Color = STREAK_REWARDS[currentDay - 1]
   *  - d7 完走 → currentDay=1 に戻り weekIndex+1
   *  - Week2(weekIndex===1)で d7 完走 → +500 Color + フラグメント 1
   */
  claimToday(): StreakClaimResult {
    if (this.hasClaimedToday()) {
      return {
        colorGained: 0,
        fragmentGained: 0,
        alreadyClaimed: true,
        newDay: this.state.currentDay,
      };
    }

    const day = this.state.currentDay;
    const colorGained = STREAK_REWARDS[day - 1] ?? 100;
    let fragmentGained = 0;
    let extraColor = 0;

    let newDay: number;
    let newWeekIndex: number;

    if (day === 7) {
      // 7 日完走 → 週を進める
      newWeekIndex = this.state.weekIndex + 1;
      newDay = 1;
      // Week2(weekIndex === 1 の完走)ボーナス
      if (this.state.weekIndex === 1) {
        extraColor = WEEK2_BONUS_COLOR;
        fragmentGained = WEEK2_BONUS_FRAGMENT;
      }
    } else {
      newDay = day + 1;
      newWeekIndex = this.state.weekIndex;
    }

    this.state = { currentDay: newDay, weekIndex: newWeekIndex, lastClaimDate: todayStr() };
    this.save();

    return {
      colorGained: colorGained + extraColor,
      fragmentGained,
      alreadyClaimed: false,
      newDay,
    };
  }

  /** スナップショット取得(UI 表示用) */
  getState(): Readonly<StreakState> {
    return { ...this.state };
  }

  /** デバッグ・テスト用: ステートをリセット */
  _reset(): void {
    this.state = this.defaultState();
    this.save();
  }
}
